import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Card, Tag, Modal, message, Input, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة
import { 
    EditOutlined, CopyOutlined, DeleteOutlined, 
    PlusOutlined, SearchOutlined, FilePdfOutlined 
} from '@ant-design/icons';
import axios from 'axios';

export default function ReportList() {
    const { t, i18n } = useTranslation();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();

    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    // 1. جلب قائمة التقارير من السيرفر
    const fetchReports = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/reports/report-templates/list');
            setReports(res.data);
        } catch (err) {
            message.error(t('reports.toasts.load_fail') + ": " + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchReports(); }, []);

    // 2. وظيفة الاستنساخ (Clone)
    const handleClone = async (report) => {
        try {
            const cloneData = { 
                ...report, 
                name: `${report.name} (${t('common.copy')})`,
                id: null 
            };
            const token = localStorage.getItem('token');
            await axios.post('/api/reports/report-templates/save', cloneData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            message.success(t('reports.toasts.clone_success'));
            fetchReports();
        } catch (err) {
            message.error(t('reports.toasts.clone_fail'));
        }
    };

    // 3. وظيفة الحذف
    const handleDelete = (id) => {
        Modal.confirm({
            title: t('reports.swal.delete_title'),
            content: t('reports.swal.delete_text'),
            okText: t('reports.swal.confirm_delete'),
            okType: 'danger',
            cancelText: t('common.cancel'),
            direction: isRTL ? 'rtl' : 'ltr',
            onOk: async () => {
                try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`/api/reports/report-templates/delete/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    message.success(t('reports.toasts.delete_success'));
                    fetchReports();
                } catch (err) {
                    message.error(t('reports.toasts.delete_fail'));
                }
            }
        });
    };

    // 4. تعريف أعمدة الجدول
    const columns = [
        {
            title: t('reports.table.name'),
            dataIndex: 'name',
            key: 'name',
            render: (text) => <span style={{ fontWeight: 'bold', color: '#005495' }}>{text}</span>
        },
        {
            title: t('reports.table.ref_obj'),
            dataIndex: 'ref_object',
            key: 'ref_object',
            render: (obj) => <Tag color="blue">{obj}</Tag>
        },
        {
            title: t('reports.table.updated'),
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: (date) => date ? new Date(date).toLocaleDateString() : '---'
        },
        {
            title: t('workflow.action'),
            key: 'actions',
            align: 'center',
            render: (_, record) => (
                <Space size="middle">
                    <Tooltip title={t('workflow.tooltips.edit')}>
                        <Button type="primary" ghost icon={<EditOutlined />} onClick={() => navigate(`/reportdesigner/${record.id}`)} />
                    </Tooltip>
                    <Tooltip title={t('common.clone')}>
                        <Button icon={<CopyOutlined />} onClick={() => handleClone(record)} />
                    </Tooltip>
                    <Tooltip title={t('workflow.tooltips.delete')}>
                        <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card 
            title={<span className="flex items-center gap-2"><FilePdfOutlined /> {t('reports.title')}</span>}
            extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/reportdesigner/new')}>
                    {t('reports.new_btn')}
                </Button>
            }
            className={isRTL ? 'font-cairo' : 'font-sans'}
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            <div style={{ marginBottom: 16, textAlign: isRTL ? 'right' : 'left' }}>
                {/* 🎯 السيرش كان بيسيب الـ Table على نفس رقم الصفحة القديم، فلو
                كنت في صفحة 3 مثلاً والفلترة رجّعت نتيجة أو اتنين، الجدول كان
                يفضل يحاول يعرض صفحة 3 (غير موجودة) فيبان فاضي - كإن السيرش
                "مش شغال". كمان الأيقونة كانت prefix ثابت (شمال) حتى في
                العربي/الأوردو، فبنعكسها لـ suffix في RTL بدل ما تفضل مكانها
                وتتعارض مع اتجاه الكتابة */}
                <Input
                    placeholder={t('reports.search_placeholder')}
                    prefix={!isRTL ? <SearchOutlined className="text-gray-400" /> : undefined}
                    suffix={isRTL ? <SearchOutlined className="text-gray-400" /> : undefined}
                    value={searchText}
                    onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    style={{ width: 300 }}
                />
            </div>

            <Table
                columns={columns}
                dataSource={reports.filter(r => r.name?.toLowerCase().includes(searchText.toLowerCase()))}
                rowKey="id"
                loading={loading}
                pagination={{
                    current: currentPage,
                    pageSize: 10,
                    onChange: (page) => setCurrentPage(page),
                    showTotal: (total, range) => `${t('dashboard.showing')} ${range[0]}-${range[1]} ${t('dashboard.of')} ${total}`
                }}
            />
        </Card>
    );
}